import { useState, useEffect, useMemo, useRef } from 'react'
import Formsy from 'formsy-react'

import {Autocomplete, TextField, Button, IconButton, Table, TableContainer, TableHead, TableBody, Paper, 
        DialogTitle, DialogContent, DialogActions, Chip, Checkbox, } from '@mui/material'
import { Add, Delete, Edit, CheckBoxOutlineBlank, CheckBox, } from '@mui/icons-material'

import { C, D, Loading, SearchField, TRow, TCell, TextFieldFormsy, ConfirmDelete } from '../include'

const oicon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

// ==========================================
// COMPONENTE: editar/adicionar filial-grupo
// ==========================================
const EditGroup = ({ row, edit, api, alert, dialogClose }) => {
  const [nome, setNome] = useState(row ? row.nome : '');
  const [embarcacoes, setEmbarcacoes] = useState([]);
  const [embarcacoesID, setEmbarcacoesID] = useState([]); 
  const [selectEmbarcacoes, setSelectEmbarcacoes] = useState([]);
  const [embarcacoesName, setEmbarcacoesName] = useState([]);
  const [grupos, setGrupos] = useState([]);

  // Atualiza as IDs baseadas nos nomes selecionados
  const getEmbarcacaoID = (selectedNames, allEmbarcacoes = embarcacoes) => {
    const idArray = allEmbarcacoes
      .filter(e => selectedNames.includes(e.nome))
      .map(e => e.id);
    setEmbarcacoesID(idArray);
  };

  // Carrega os dados iniciais do Modal
  useEffect(() => {
    // Carrega a lista de grupos em paralelo
    api.get(`/Grupos`).then(res => setGrupos(res)).catch(e => console.error(e));

    // Carrega as embarcações
    const fetchEmbarcacoes = async () => {
      try {
        const response = await api.get('/embarcacoesList');
        const embarcacoesArray = response.map(el => el.nome);
        
        setEmbarcacoes(response);
        setEmbarcacoesName(embarcacoesArray);

        // Se for edição, busca quais já estão atreladas a este grupo
        if (edit && row) {
          const data = await api.get(`/Grupo/Embarcacoes?selectGroups=${JSON.stringify([row.id])}`);
          
          const embarcacoesSelectNome = data.map(value => value.nome);
          const embarcacoesSelectID = data.map(value => value.embarcacao_id);
          
          setSelectEmbarcacoes(embarcacoesSelectNome);
          setEmbarcacoesID(embarcacoesSelectID);
        }
      } catch (err) {
        console.error('Erro ao carregar embarcacoes', err);
      }
    };

    fetchEmbarcacoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Montagem inicial

  // O equivalente ao componentDidUpdate para reagir à mudança da prop row
  useEffect(() => {
    if (edit && row && embarcacoes.length > 0) {
      api.get(`/Grupo/Embarcacoes?selectGroups=${JSON.stringify([row.id])}`)
        .then(data => {
          setSelectEmbarcacoes(data.map(value => value.nome));
          setEmbarcacoesID(data.map(value => value.embarcacao_id));
        })
        .catch(console.error);
    }
  }, [row, edit, api, embarcacoes.length]);


  const handleSave = () => {
    const payload = {
      nome: nome,
      embarcacoes: JSON.stringify(embarcacoesID)
    };

    if (!edit) {
      api.post('/Grupo', payload)
        .then(r => {         
          dialogClose();
          alert(r.msg || 'Operação concluída.');
        })
        .catch(e => alert(e.msg || 'Ocorreu um erro ao criar a Filial-Grupo !'));
    } else {
      payload.grupo_id = row.id; // Adiciona ID para o update
      api.post('/GrupoUpdate', payload)
        .then(r => {
          dialogClose();
          alert(r.msg || 'Operação concluída.');
        })
        .catch(e => alert(e.msg || 'Ocorreu um erro ao salvar alterações !'));
    }
  };

  const isReserved = ['indeterminado', 'fora de operação'].includes(String(nome).toLowerCase());
  const isIndeterminado = String(nome || '').trim().toLowerCase() === 'indeterminado';

  return (
    <Formsy onValidSubmit={() => null}>
      <DialogTitle style={{ minWidth: '400px' }}>
        {edit ? `Editar ${C.groupLabel}` : `Adicionar ${C.groupLabel}`}
      </DialogTitle>
      
      <DialogContent>
        <D h="1rem" />
        <TextFieldFormsy 
          value={nome} 
          onChange={v => setNome(v)} 
          type="text" 
          name="name" 
          label="Nome" 
          variant="outlined" 
          required 
          disabled={isReserved}
          InputProps={{
            readOnly: isReserved,        
            inputProps: {
              style: { userSelect: "none" } 
            }
          }}
        />
        
        <D h="1rem" />
        <Autocomplete
          multiple
          options={embarcacoesName}
          value={selectEmbarcacoes}
          getOptionLabel={(opt) => opt || ''}
          isOptionEqualToValue={(opt, val) => opt === val}
          renderTags={(value, getTagProps) => value.map((option, index) => { 
            const tagProps = getTagProps({ index });
            return (
              <Chip
                {...tagProps}
                label={option}
                deleteIcon={isIndeterminado ? null : tagProps.deleteIcon}
                onDelete={isIndeterminado ? undefined : tagProps.onDelete}
                key={index} // Key recomendada ao mapear
              />
            );
          })}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={oicon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (<TextField {...params} label="Embarcações" placeholder="" />)}
          onChange={(e, value) => {
            setSelectEmbarcacoes(value);
            getEmbarcacaoID(value);
          }}
        />
        <D h="2rem" />
      </DialogContent>
      
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>
          Cancelar
        </Button>
        <Button variant="outlined" color="primary" onClick={handleSave}>
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: Lista de grupos cadastrados
// ==========================================
export const Grupo = ({ api, alert, dialog, dialogClose, permission }) => {
  const [searchField, setSearchField] = useState('');
  const [dataTable, setDataTable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // 1. Criar a referência inicial
  const isFirstRender = useRef(true);

  const updateData = () => {
    setIsLoading(true);
    api.get('/Grupos', { params: { log: true } })
      .then(r => {
        setDataTable(r);
        setIsLoading(false);
      })
      .catch(e => {
        alert("Erro ao buscar dados.");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      updateData();
      isFirstRender.current = false;
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditDialog = (row = null, isEdit = false) => {
    dialog(
      <EditGroup 
        edit={isEdit} 
        row={row} 
        api={api} 
        alert={alert} 
        dialogClose={() => {
          dialogClose();
          updateData();
        }} 
      />
    );
  };

  const openDeleteDialog = (row) => {
    dialog(
      <ConfirmDelete
        msg="Tem certeza que deseja excluir a Filial-Grupo:"
        itemName={row.nome}
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete(`/Grupo?nome=${row.nome}`)
            .then(r => {
              alert(r.msg);
              updateData(); 
              dialogClose();
            })
            .catch(e => alert(e.msg || 'Erro ao excluir a Filial-Grupo.'));
        }}
      />
    );
  };

  // Otimização do filtro na memória
  const filteredData = useMemo(() => {
    if (!searchField) return dataTable;
    
    const searchLower = searchField.toLowerCase().replace(/\s/g, '');
    return dataTable.filter(row => {
      const rowValues = Object.values(row).map(v => String(v)).join('');
      return rowValues.toLowerCase().replace(/\s/g, '').includes(searchLower);
    });
  }, [dataTable, searchField]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF">
            <SearchField 
              value={searchField} 
              onChange={v => setSearchField(v)} 
            />
            <D e />
            <D size="2rem" />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => openEditDialog(null, false)}
            >
              <Add /> {C.groupLabel}
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                {permission === 'w' && <TCell align="center">Editar</TCell>}
                {permission === 'w' && <TCell align="center">Excluir</TCell>}
                <TCell width="100%">{C.groupLabel} ({dataTable.length} grupos)</TCell>
              </TRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TRow>
                  <TCell component="th" align="center" colSpan={3}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : filteredData.length > 0 ? (
                filteredData.map(row => {
                  const isReserved = ['indeterminado', 'fora de operação'].includes(String(row.nome).toLowerCase());
                  
                  return (
                    <TRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {permission === 'w' && (
                        <TCell align="center" component="th">
                          <IconButton 
                            style={{ color: '#aaa' }} 
                            onClick={() => openEditDialog(row, true)}
                          >
                            <Edit />
                          </IconButton>
                        </TCell>
                      )}

                      {permission === 'w' && (
                        isReserved ? (
                          <TCell align="center" component="th">
                            <div style={{ width: 40 }} />
                          </TCell>
                        ) : (
                          <TCell align="center" component="th">
                            <IconButton
                              style={{ color: '#aaa' }}
                              onClick={() => openDeleteDialog(row)}
                            >
                              <Delete />
                            </IconButton>
                          </TCell>
                        )
                      )}
                      
                      <TCell component="th">{row.nome}</TCell>
                    </TRow>
                  );
                })
              ) : (
                <TRow>
                  <TCell component="th" align="center" colSpan={3}>
                    Nenhum Grupo encontrado
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};